import OfferInclusionsList from 'components/Common/Inclusions/OfferInclusionsList'
import Modal from 'LuxKit/Modal/Modal'
import Group from 'components/utils/Group'
import ModalContext from 'contexts/ModalContext'
import useOfferUrl from 'hooks/Offers/useOfferUrl'
import { useOfferInclusions } from 'hooks/useOfferInclusions'
import { EmptyArray } from 'lib/array/arrayUtils'
import LuxPlusLabelWithModal from 'luxPlus/components/LuxPlusLabelWithModal'
import { useCallback, useContext, useMemo } from 'react'
import { useIntl } from 'react-intl'
import ExperimentalSearchTileFeaturesList, { MAX_INCLUSIONS_TO_SHOW } from '../shared/ExperimentalSearchTileFeaturesList'
import type { SearchTileRowInclusion } from '../shared/SearchTileRowInclusionItem'
import useOfferMetaData from 'hooks/Offers/useOfferMetaData'
import useProperty from 'hooks/Offers/useProperty'
import useBedbankRates from 'hooks/useBedbankRates'
import TextLoadingBox from 'LuxKit/Typography/TextLoadingBox'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import ExperimentalHotelUpgradableInclusionsLabel from './ExperimentalHotelUpgradableInclusionsLabel'
import SearchTileRoomBreakdown from '../shared/SearchTileRoomBreakdown'
import { useAppSelector } from 'hooks/reduxHooks'
import { getCanViewLuxPlusBenefits } from 'luxPlus/selectors/featureToggle'
import LuxPlusInclusions from '../../../../../luxPlus/components/LuxPlusInclusions'
import LoyaltyInclusions from 'loyalty/components/LoyaltyInclusions'
import useLoyaltyBenefitInclusions from 'loyalty/hooks/useLoyaltyBenefitInclusions'
import { mapPropertyPopularFacilitiesToAmenities } from './propertyPopularFacilities'
import { useActiveInclusionFilters } from 'hooks/useActiveInclusionFilters'
import { reorderTileInclusionsByActiveFilters } from 'lib/offer/inclusionUtils'
import { isFlashHotelOffer } from 'lib/offer/offerTypes'
import { CHARTER_FLIGHT_DETAILS, MALDIVES_CHARTER_BUNDLE_CAMPAIGN_TAG } from 'constants/charterFlight'
import { rewriteCharterReturnFlightsGuestInclusions } from 'lib/charterFlights/charterBundleOfferPageUtils'

const DEFAULT_CHARTER_SEARCH_ROOMS: Array<App.Occupants> = [{ adults: 2 }]

interface Props {
  bestPackage?: App.HotelPackage
  filters?: App.OfferListFilters
  offer: App.HotelOffer | App.HotelOfferSummary
  offerMetaData?: App.OfferListMetaData
  maxInclusionsToShow?: number
  hideSecondaryLuxPlusPromos?: boolean
  hideLuxPlusLabels?: boolean
  hideUpgradableInclusionsLabel?: boolean
  hideRoomBreakdown?: boolean
  maxAmenitiesToShow?: number
}

function ExperimentalHotelSearchTileFeatures({
  bestPackage,
  filters,
  offer,
  offerMetaData,
  maxInclusionsToShow,
  hideSecondaryLuxPlusPromos = false,
  hideLuxPlusLabels = false,
  hideUpgradableInclusionsLabel = false,
  hideRoomBreakdown = false,
  maxAmenitiesToShow,
}: Props) {
  const intl = useIntl()
  const metaData = useOfferMetaData(offer.id, filters)
  const [propertyContent, isFetchingPropertyContent] = useProperty(metaData?.accommodationId)

  const { inclusions, bonusInclusions, luxPlusInclusions } = useOfferInclusions(
    bestPackage,
    {
      checkIn: filters?.checkIn,
      checkOut: filters?.checkOut,
    },
  )

  const isBedbankRate = metaData?.pricing.rateType === 'bedbank'

  const [{ hotelOnlyRates: bedbankRate }, isFetchingBedbankRates] = useBedbankRates(
    isBedbankRate ? metaData?.pricing.ratePropertyId : undefined,
    filters?.rooms,
    filters?.checkIn,
    filters?.checkOut,
    isBedbankRate,
  )

  const bedbankRateForRoom = useMemo(() => {
    if (!isBedbankRate) return undefined
    const roomRateId = metaData?.pricing.lowestPriceRoomRateId
    if (!roomRateId || !bedbankRate.length) return undefined

    return bedbankRate.flatMap(rates => rates).find(rate => rate.id === roomRateId)
  }, [isBedbankRate, metaData?.pricing.lowestPriceRoomRateId, bedbankRate])

  const bedbankInclusions = useMemo<Array<App.OfferInclusion>>(() => {
    if (!bedbankRateForRoom?.inclusions?.length) return EmptyArray
    return bedbankRateForRoom.inclusions.filter(inclusion => inclusion.description !== 'Room only')
  }, [bedbankRateForRoom?.inclusions])

  const isLuxPlusMember = useAppSelector(getCanViewLuxPlusBenefits)
  const activeInclusionCategories = useActiveInclusionFilters()
  const isFlashHotel = isFlashHotelOffer(offer)
  const charterRouteInfo = CHARTER_FLIGHT_DETAILS['melbourne-maldives']
  const isCharterBundleOffer = offer.campaignTags.includes(MALDIVES_CHARTER_BUNDLE_CAMPAIGN_TAG)

  const getEffectiveInclusions = useCallback(<T extends { description: string; symbol?: string }>(inclusions: Array<T>): Array<T> => {
    if (!isCharterBundleOffer) {
      return inclusions
    }

    const { originAirport, destinationAirport } = charterRouteInfo
    return rewriteCharterReturnFlightsGuestInclusions(inclusions, {
      rooms: filters?.rooms?.length ? filters.rooms : DEFAULT_CHARTER_SEARCH_ROOMS,
      originCity: originAirport.cityDesc,
      destinationCity: destinationAirport.cityDesc,
      intl,
    })
  }, [charterRouteInfo, filters?.rooms, intl, isCharterBundleOffer])

  const luxPlusInclusionsThatCanBeDisplayed = useMemo<Array<App.OfferInclusion>>(() => {
    return isLuxPlusMember ? luxPlusInclusions : EmptyArray
  }, [isLuxPlusMember, luxPlusInclusions])

  const tileInclusions = useMemo<Array<SearchTileRowInclusion>>(() => {
    if (!isFlashHotel) return EmptyArray
    if (!offer.tileInclusions?.length) return EmptyArray
    return reorderTileInclusionsByActiveFilters(getEffectiveInclusions(offer.tileInclusions), activeInclusionCategories)
  }, [activeInclusionCategories, getEffectiveInclusions, isFlashHotel, offer.tileInclusions])

  const hasOfferInclusions = bonusInclusions.length > 0 || inclusions.length > 0
  const isFetchingPropertyAmenities = !!metaData?.accommodationId &&
    isFetchingPropertyContent &&
    !propertyContent &&
    !bedbankInclusions.length &&
    !tileInclusions.length &&
    !hasOfferInclusions

  const propertyContentAmenities = useMemo<Array<SearchTileRowInclusion>>(() => {
    if (!propertyContent?.popularFacilities.length) return EmptyArray
    return mapPropertyPopularFacilitiesToAmenities(propertyContent.popularFacilities)
  }, [propertyContent?.popularFacilities])

  const featuresToDisplay = useMemo<{
    featureList: Array<SearchTileRowInclusion>
    featureType: 'inclusions' | 'amenities'
  }>(() => {
    if (bedbankInclusions.length > 0) {
      return { featureList: bedbankInclusions, featureType: 'inclusions' }
    }

    if (hasOfferInclusions) {
      return {
        featureList: getEffectiveInclusions([...inclusions, ...bonusInclusions]),
        featureType: 'inclusions',
      }
    }

    if (tileInclusions.length > 0) {
      return { featureList: tileInclusions, featureType: 'inclusions' }
    }

    const fallbackAmenities = offer.lowestPricePackage?.roomType.amenities ?? EmptyArray
    const amenities = propertyContentAmenities.length > 0 ? propertyContentAmenities : fallbackAmenities
    if (amenities.length > 0) return { featureList: amenities, featureType: 'amenities' }

    return { featureList: EmptyArray, featureType: 'inclusions' }
  }, [bedbankInclusions, bonusInclusions, getEffectiveInclusions, hasOfferInclusions, inclusions, offer.lowestPricePackage?.roomType.amenities, propertyContentAmenities, tileInclusions])

  const loyaltyCount = useLoyaltyBenefitInclusions(offer).length
  const maxFeaturesToShow = featuresToDisplay.featureType === 'amenities' ? maxAmenitiesToShow : maxInclusionsToShow
  const showLoyaltyInline = featuresToDisplay.featureType === 'amenities' ||
    featuresToDisplay.featureList.length + loyaltyCount <= MAX_INCLUSIONS_TO_SHOW

  const offerUrl = useOfferUrl(offer, {
    filters,
    offerLinkIncludesFilters: true,
    bundledOfferId: offerMetaData?.bundledOfferId,
  })

  const showModal = useContext(ModalContext)
  const openAllInclusionsModal = useCallback(() => {
    showModal(<Modal
      title={offer.property.name}
      primaryActionText={intl.formatMessage({
        id: '5n7AcIxL6sr_rLtKdVWM',
        defaultMessage: 'View offer',
        description: 'CTA button in experimental hotel inclusions modal to view the offer',
      })}
      primaryActionTo={offerUrl}
    >
      <Group direction="vertical" gap={12}>
        <Group direction="vertical" gap={4}>
          <OfferInclusionsList inclusions={featuresToDisplay.featureList} />
          <LoyaltyInclusions offer={offer} variant="offer-page" />
        </Group>
      </Group>
    </Modal>)
  }, [offer, featuresToDisplay, offerUrl, showModal, intl])

  const upgradableInclusions = useMemo<Array<App.OfferInclusion>>(() => {
    if (!offer.packages.length) return EmptyArray

    // Find the package with the most inclusions
    const packageWithMostInclusions = offer.packages.reduce((max, pkg) => {
      const maxCount = (max.inclusions?.length ?? 0) + (max.bonusInclusions?.length ?? 0)
      const pkgCount = (pkg.inclusions?.length ?? 0) + (pkg.bonusInclusions?.length ?? 0)
      return pkgCount > maxCount ? pkg : max
    }, offer.packages[0])

    const mostInclusions = [
      ...(packageWithMostInclusions.inclusions ?? []),
      ...(packageWithMostInclusions.bonusInclusions ?? []),
    ]
    if (!mostInclusions.length) return EmptyArray

    const inclusionsToDisplay = featuresToDisplay.featureType === 'inclusions' ? featuresToDisplay.featureList : []
    // Filter out inclusions that are already displayed or duplicated
    const displayedDescriptions = new Set(inclusionsToDisplay.map(i => i.description))
    const seenDescriptions = new Set<string>()

    const upgradableInclusions = mostInclusions.filter(inclusion => {
      const desc = inclusion.description
      if (displayedDescriptions.has(desc) || seenDescriptions.has(desc)) return false
      seenDescriptions.add(desc)
      return true
    })

    if (
      upgradableInclusions.length === 1 &&
      upgradableInclusions[0].description.toLowerCase().includes('breakfast')
    ) {
      return EmptyArray
    }

    return upgradableInclusions.length ? upgradableInclusions : EmptyArray
  }, [offer.packages, featuresToDisplay])

  const saveAmount = !!metaData?.pricing?.lowestMemberPrice && !!metaData?.pricing?.lowestPrice ?
    metaData?.pricing?.lowestPrice - metaData?.pricing?.lowestMemberPrice : undefined
  const isFetchingSearchTileFeatures = (isBedbankRate && isFetchingBedbankRates) || isFetchingPropertyAmenities

  return (
    <Group className={ExperimentalHotelSearchTileFeatures.name} direction="vertical" verticalAlign="space-between" grow gap={8} noDisplayWhenEmpty>
      <Group className={ExperimentalHotelSearchTileFeatures.name} direction="vertical" gap={8} noDisplayWhenEmpty>
        {isFetchingSearchTileFeatures && (
          <VerticalSpacer gap={4}>
            <TextLoadingBox typography="body-small" width="60%" />
            <TextLoadingBox typography="body-small" width="80%" />
            <TextLoadingBox typography="body-small" width="40%" />
          </VerticalSpacer>
        )}
        {!!luxPlusInclusionsThatCanBeDisplayed?.length && !hideSecondaryLuxPlusPromos && <LuxPlusInclusions
          luxPlusInclusions={luxPlusInclusionsThatCanBeDisplayed}
          accommodationOffer={offer}
          hideUpsellModal={!!offer.luxPlusTileInclusions?.length}
          showMinStayDescription
          onShowMoreClick={openAllInclusionsModal}
        />}
        {!isFetchingSearchTileFeatures && (
          <>
            <Group direction="vertical" gap={4}>
              <ExperimentalSearchTileFeaturesList
                features={featuresToDisplay.featureList}
                featuresType={featuresToDisplay.featureType}
                additionalHiddenCount={showLoyaltyInline ? 0 : loyaltyCount}
                maxInclusionsToShow={maxFeaturesToShow}
                onMoreClick={openAllInclusionsModal}
              />
              {showLoyaltyInline && <LoyaltyInclusions offer={offer} variant="search-tile" />}
            </Group>

            {!hideLuxPlusLabels && !hideSecondaryLuxPlusPromos && !!luxPlusInclusions?.length && !isLuxPlusMember && (
              <LuxPlusLabelWithModal
                type="member-inclusions"
                inclusions={luxPlusInclusions}
                offer={offer}
                hideUpsellModal={!!offer.luxPlusTileInclusions?.length}
              />
            )}
            {!hideLuxPlusLabels && !hideSecondaryLuxPlusPromos && !!saveAmount && saveAmount > 0 && !luxPlusInclusions?.length && (
              <LuxPlusLabelWithModal
                type="member-price-offer"
                offer={offer}
                saveAmount={saveAmount}
              />
            )}
            {!hideUpgradableInclusionsLabel && !hideSecondaryLuxPlusPromos && !!upgradableInclusions?.length && <ExperimentalHotelUpgradableInclusionsLabel offer={offer} inclusions={upgradableInclusions} filters={filters} />}
          </>
        )}
      </Group>
      {!hideRoomBreakdown && <SearchTileRoomBreakdown offer={offer} filters={filters} />}
    </Group>
  )
}

export default ExperimentalHotelSearchTileFeatures
